import { ActionButtons } from '@components/ActionButtons/ActionButtons'
import BalanceChange from '@components/BalanceChange/BalanceChange'
import ChainLogo from '@components/ChainLogo'
import ChainPicker from '@components/ChainPicker'
import PerpBalanceRow from '@components/PerpBalanceRow'
import PortfolioChart from '@components/PortfolioChart'
import Promo from '@components/Promo'
import { SidebarMobileToggle } from '@components/Sidebar/MobileToggle'
import { TokenList } from '@components/TokenList/TokenList'
import Touchable from '@components/Touchable'
import Portfolio from '@modules/BorrowLend/Portfolio'
import { useLiquidiumBalances } from '@modules/BorrowLend/useLiquidiumBalances'
import { LLMChatButton } from '@modules/LLMChat/LLMChatButton'
import { DEFAULT_L1_CHAINS } from '@plug-core/constants/business'
import useBalances from '@plug-core/hooks/useBalances'
import useEvmChain from '@plug-core/hooks/useEVMChain'
import useLiquidium from '@plug-core/hooks/useLiquidium'
import { useSelectedChain, useUserEVMChains } from '@plug-core/state'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { Button, Heading, Surface } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDown } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import { TokenListButtons } from './TokenListButtons'

export const HomeOverview: FC = () => {
  const navigate = useNavigate()

  const { groupedBalances, filteredBalances, perpsAccountBalance } = useBalances()
  const { selectedChain, setSelectedChain } = useSelectedChain()
  const { t } = useTranslation()
  const { userEVMChains } = useUserEVMChains()
  const currentEvmChain = useEvmChain(selectedChain)
  const { healthFactor } = useLiquidium()
  const liquidiumBalances = useLiquidiumBalances()

  const [chainSelectVisible, setChainSelectVisible] = useState(false)

  const totalUsdBalance = groupedBalances
    .map((balance) => balance.usdBalance || 0)
    .reduce((acc, balance) => acc + balance, 0)

  return (
    <div className="flex flex-col">
      <SidebarMobileToggle title="Home" />

      <LLMChatButton />

      <div className="flex flex-1 flex-col">
        <Surface padding="none" className="px-6 py-4">
          <div className="my-2 flex flex-col items-center gap-1">
            <Heading level={1} tabular className="text-[40px] sm:text-[44px]">
              {formatMoney(totalUsdBalance)}
            </Heading>

            <BalanceChange totalUsdBalance={totalUsdBalance} />
          </div>

          {totalUsdBalance > 0 && (
            <PortfolioChart balances={filteredBalances} totalUsdBalance={totalUsdBalance} />
          )}
        </Surface>

        <div className="mt-4">
          <ActionButtons />
        </div>

        <Promo />

        {!!(liquidiumBalances.borrowed || liquidiumBalances.supplied) && (
          <div className="mt-4 flex flex-col gap-2">
            <Heading level={5} className="ml-2">
              {t('home.borrowLend')}
            </Heading>
            <Touchable onClick={() => navigate(ROUTES.BORROW_LEND)}>
              <Portfolio
                balances={liquidiumBalances}
                healthFactor={healthFactor}
                newHealthFactor={undefined}
              />
            </Touchable>
          </div>
        )}

        {!!perpsAccountBalance?.assetPositions?.length && (
          <div className="mt-4 flex flex-col">
            <div className="mx-2 mb-1 flex items-center justify-between">
              <Heading level={5}>{t('home.perps')}</Heading>

              <Button variant="link" size="sm" onClick={() => navigate(ROUTES.PERPS)}>
                {t('common.manage')}
              </Button>
            </div>

            {perpsAccountBalance.assetPositions.map((position) => (
              <PerpBalanceRow key={position.position.coin} position={position} />
            ))}
          </div>
        )}

        <div className="mt-4 mb-2 flex items-center justify-between">
          <Heading level={5} className="ml-2">
            Tokens
          </Heading>
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setChainSelectVisible(true)}
            className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-pill border border-solid border-hairline bg-surface py-1.5 pr-2 pl-2.5 font-text text-[14px] font-semibold text-fg transition-[background,transform] duration-150 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus active:scale-[0.98] motion-reduce:active:scale-100"
          >
            <ChainLogo chain={selectedChain} size={20} />
            {currentEvmChain?.name || t(`chains.long.${selectedChain}`)}
            <HiChevronDown aria-hidden="true" className="size-5 text-fg-2" />
          </button>

          <ChainPicker
            chains={['all', ...DEFAULT_L1_CHAINS, ...userEVMChains.map(String)]}
            initialChain={selectedChain}
            chainSelectVisible={chainSelectVisible}
            setChainSelectVisible={setChainSelectVisible}
            onChainChange={setSelectedChain}
            addNewButton={true}
          />
        </div>

        <TokenList
          balances={groupedBalances}
          onTokenClick={(token) => navigate(ROUTES.TOKEN_DETAILS(token.token.id))}
          showPriceChange
          separated
        />
        <TokenListButtons />
      </div>
    </div>
  )
}
